import { useEffect, useRef, useState } from 'react'
import { Crop } from 'lucide-react'
import { Modal } from './primitives.js'
import type { CropFraction } from './media-staging.js'

/**
 * CROPPER LIVE CONTROL (LOT 5.5.2) — cadrage carré simple : déplacer,
 * zoomer légèrement, aperçu en direct. Sert au logo compact, à l'avatar et
 * à l'Apple Touch Icon — JAMAIS au logo horizontal principal.
 *
 * Le recadrage est calculé ici en FRACTIONS de l'image source et appliqué
 * CÔTÉ SERVEUR (sharp) sur l'original — l'aperçu n'est qu'un aperçu.
 */

const VIEW = 300 // côté du viewport de cadrage (px)

export function CropperModal(props: {
  open: boolean
  file: File
  title: string
  round?: boolean
  onClose: () => void
  onExited: () => void
  onDone: (crop: CropFraction) => void
}) {
  const [url] = useState(() => URL.createObjectURL(props.file))
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null)
  const [zoom, setZoom] = useState(1)
  const [off, setOff] = useState({ x: 0, y: 0 })
  const drag = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null)

  useEffect(() => () => URL.revokeObjectURL(url), [url])

  const baseScale = nat ? Math.max(VIEW / nat.w, VIEW / nat.h) : 1
  const scale = baseScale * zoom
  const dispW = (nat?.w ?? 0) * scale
  const dispH = (nat?.h ?? 0) * scale

  const clamp = (x: number, y: number) => ({
    x: Math.min(0, Math.max(VIEW - dispW, x)),
    y: Math.min(0, Math.max(VIEW - dispH, y)),
  })

  useEffect(() => { setOff((o) => clamp(o.x, o.y)) }, [zoom, nat]) // eslint-disable-line react-hooks/exhaustive-deps

  const onPointerDown = (e: React.PointerEvent) => {
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    drag.current = { px: e.clientX, py: e.clientY, ox: off.x, oy: off.y }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return
    setOff(clamp(drag.current.ox + (e.clientX - drag.current.px), drag.current.oy + (e.clientY - drag.current.py)))
  }
  const onPointerUp = () => { drag.current = null }

  const valider = () => {
    if (!nat) return
    // Fenêtre visible, en pixels source.
    const leftPx = -off.x / scale
    const topPx = -off.y / scale
    const sidePx = VIEW / scale
    props.onDone({
      x: leftPx / nat.w,
      y: topPx / nat.h,
      size: sidePx / Math.min(nat.w, nat.h),
    })
  }

  return (
    <Modal open={props.open} onClose={props.onClose} onExited={props.onExited} ariaLabel={props.title} width={420}>
      <header className="lc-modal__head">
        <span className="lc-modal__icon"><Crop size={20} /></span>
        <div className="lc-modal__title">{props.title}</div>
      </header>
      <div className="lc-modal__body">
        <p className="lc-card__desc" style={{ marginBottom: 'var(--sp-4)' }}>
          Déplacez l’image et ajustez le zoom — la zone visible sera conservée.
        </p>
        <div className="lc-crop-stage">
          <div
            className={`lc-crop-view${props.round ? ' lc-crop-view--round' : ''}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <img
              src={url}
              alt=""
              draggable={false}
              onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
              style={{
                width: dispW || undefined, height: dispH || undefined,
                transform: `translate(${off.x}px, ${off.y}px)`,
              }}
            />
          </div>
        </div>
        <label className="lc-crop-zoom">
          <span className="lc-label">Zoom</span>
          <input type="range" min={1} max={3} step={0.01} value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))} aria-label="Zoom du cadrage" />
        </label>
        <div className="lc-dialog__actions">
          <button className="lc-btn lc-btn--ghost" onClick={props.onClose}>Annuler</button>
          <button className="lc-btn lc-btn--primary" disabled={!nat} onClick={valider}>Valider le cadrage</button>
        </div>
      </div>
    </Modal>
  )
}
